import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  api,
  CreateDocumentTagDto,
  DocumentTagDto,
  DocumentTagsListDto,
} from '@sib/api';
import { QueryKey } from '@sib/api/enums';
import { PaginationOptionsWithSortArray } from '@sib/api/types';

const CACHE_TIME = 5000;

export const useDocumentTagsList = (
  organizationId: string,
  query: PaginationOptionsWithSortArray,
  options?: Omit<UseQueryOptions<DocumentTagsListDto>, 'queryKey'>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [QueryKey.Resource.DocumentTags, query, organizationId],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organizationId.documentTagsControllerFindAll(
        organizationId || '',
        query
      ),
  });

export type UseCreateTagOptions = UseMutationOptions<
  DocumentTagDto,
  Error,
  CreateDocumentTagDto
> & {
  organizationId: string;
};

export const useCreateDocumentTag = ({
  organizationId,
  ...options
}: UseCreateTagOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,

    mutationFn: async data =>
      api.organizationId.documentTagsControllerCreate(organizationId, data),
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.DocumentTags],
      });
      options?.onSuccess?.(...args);
    },
  });
};

export interface DeleteDocumentTagPayload {
  organizationId: string;
  tagId: string;
}

export const useDeleteDocumentTag = (
  options?: UseMutationOptions<unknown, Error, DeleteDocumentTagPayload>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.DocumentTags],
      });
      options?.onSuccess?.(...args);
    },
    mutationFn: async ({ tagId, organizationId }) =>
      api.organizationId.documentTagsControllerDeleteOne(tagId, organizationId),
  });
};
